// ABOUTME: Rich-text form field backed by Quill (react-quill), always editable in place.
// ABOUTME: Typing stays local to the editor; the form value is written debounced and flushed on blur.
import 'react-quill/dist/quill.snow.css';

import { Box, FormControl, Stack, Typography } from '@ds/coolshop';
import { FieldProps } from '@src/assets/types';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import ReactQuill, { Quill } from 'react-quill';

import { enhanceContent } from './HtmlFieldElement/api';

// Register custom AI enhancement icon
const icons = Quill.import('ui/icons');
icons['ai-enhance'] = '✨';

const BASE_TOOLBAR = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ header: 1 }, { header: 2 }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ direction: 'rtl' }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
    ['clean'],
];

const WRITE_DEBOUNCE_MS = 300;

function normalizeHtml(htmlString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    doc.querySelectorAll('[style]').forEach(element => {
        if (element instanceof HTMLElement) {
            const styles = Array.from(element.style);
            const sortedStyles = styles
                // remove transparent background style causing spacing issues (PIM-534)
                .filter(
                    prop =>
                        prop !== 'background-color' &&
                        element.style.getPropertyValue(prop) !== 'transparent',
                )
                .sort()
                .map(prop => `${prop}: ${element.style.getPropertyValue(prop)};`)
                .join(' ');
            element.setAttribute('style', sortedStyles);
        }
    });

    return doc.body.innerHTML;
}

/** The form value of an editor content: normalized HTML, empty when the editor is empty. */
function toFieldValue(content: string) {
    const normalizedContent = normalizeHtml(content);
    return normalizedContent !== '<p><br></p>' ? normalizedContent : '';
}

const HtmlFieldElement = ({
    name,
    control,
    label,
    isMultiLanguage,
    position,
    readOnly,
    readOnlyMessage,
    disabled,
    LanguageComponent,
    onChange = () => {},
}: FieldProps['html']): JSX.Element => {
    const { field } = useController({
        name,
        control,
    });

    const reactQuillRef = useRef<ReactQuill>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    // Raw Quill HTML: ReactQuill resets the editor whenever `value` differs from its own content,
    // so the editor is fed what it emitted, not the normalized form value.
    const [localValue, setLocalValue] = useState<string>(field.value || '');
    // Last value written into the form: a form value that differs from it is an external change.
    const lastWrittenRef = useRef<string>(field.value || '');
    // Raw editor content typed since the last form write, waiting for the trailing write.
    const pendingContentRef = useRef<string | null>(null);
    const writeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isDisabled = useMemo(
        () => disabled || readOnly || field.disabled,
        [disabled, readOnly, field.disabled],
    );
    const { enableAI } = useContext(SettingsContext);

    // Build toolbar with conditional AI button
    const toolbar = useMemo(
        () => (enableAI ? [...BASE_TOOLBAR, ['ai-enhance']] : BASE_TOOLBAR),
        [enableAI],
    );

    useEffect(() => {
        if (!reactQuillRef.current) return;
        const quillInstance = reactQuillRef.current.getEditor();
        const toolbarModule = quillInstance.getModule('toolbar');
        toolbarModule.container.style.zIndex = position ? `9${position}` : '9';
        toolbarModule.container.style.position = 'relative';
    }, [position]);

    // Disable toolbar buttons when field is disabled
    useEffect(() => {
        if (!reactQuillRef.current) return;
        const quillInstance = reactQuillRef.current.getEditor();
        const toolbarModule = quillInstance.getModule('toolbar');
        toolbarModule.container.style.pointerEvents = isDisabled ? 'none' : 'auto';
    }, [isDisabled]);

    // Every form write re-renders the whole EditNode form (FormElement watches all fields), so
    // writes are kept out of the keystroke path. The latest callbacks live in a ref because the
    // debounced write and the unmount flush run outside the render that scheduled them.
    const writeToFormRef = useRef<(fieldValue: string) => void>(() => {});
    writeToFormRef.current = (fieldValue: string) => {
        lastWrittenRef.current = fieldValue;
        field.onChange(fieldValue);
        onChange(fieldValue);
    };

    const cancelPendingWrite = useCallback(() => {
        if (writeTimeoutRef.current) {
            clearTimeout(writeTimeoutRef.current);
            writeTimeoutRef.current = null;
        }
        pendingContentRef.current = null;
    }, []);

    // Writes the content still waiting for the debounce. Runs on blur too, so a click on the save
    // button (which blurs the editor first) always submits the last typed character.
    const flushPendingWrite = useCallback(() => {
        const pendingContent = pendingContentRef.current;
        cancelPendingWrite();
        if (pendingContent !== null) {
            writeToFormRef.current(toFieldValue(pendingContent));
        }
    }, [cancelPendingWrite]);

    useEffect(() => flushPendingWrite, [flushPendingWrite]);

    // Sync the editor with form values changed from outside (form reset, node switch): a typed
    // content still waiting for its write belongs to the previous value and is dropped.
    useEffect(() => {
        const currentFieldValue = field.value || '';
        if (currentFieldValue === lastWrittenRef.current) return;
        cancelPendingWrite();
        lastWrittenRef.current = currentFieldValue;
        setLocalValue(currentFieldValue);
    }, [field.value, cancelPendingWrite]);

    const handleAIEnhance = useCallback(async () => {
        if (!reactQuillRef.current || isProcessing) return;

        try {
            setIsProcessing(true);

            // Get current content from Quill editor
            const quillInstance = reactQuillRef.current.getEditor();
            const currentContent = quillInstance.root.innerHTML;

            // Call the enhancement API
            const enhancedContent = await enhanceContent(currentContent);
            const fieldValue = toFieldValue(enhancedContent);

            // The enhanced content replaces whatever was still waiting for its write
            cancelPendingWrite();
            setLocalValue(fieldValue);
            writeToFormRef.current(fieldValue);
        } catch (error) {
            SnackBarUtils.error(
                error instanceof Error ? error.message : 'Failed to enhance content',
            );
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const handleTextChange = (content: string, _delta: any, source: string) => {
        setLocalValue(content);
        if (source !== 'user') return;

        // The first change of a typing burst is written at once, so the form turns dirty (and
        // the save button enabled) immediately; the rest of the burst is written when it pauses.
        if (writeTimeoutRef.current === null) {
            writeToFormRef.current(toFieldValue(content));
        } else {
            clearTimeout(writeTimeoutRef.current);
            pendingContentRef.current = content;
        }
        writeTimeoutRef.current = setTimeout(flushPendingWrite, WRITE_DEBOUNCE_MS);
    };

    const modules = useMemo(
        () => ({
            toolbar: {
                container: toolbar,
                handlers: enableAI
                    ? {
                          'ai-enhance': handleAIEnhance,
                      }
                    : undefined,
            },
            clipboard: { matchVisual: false },
        }),
        [toolbar, enableAI, handleAIEnhance],
    );

    return (
        <Box data-testid={`html-field-${name}`} position="relative" onBlur={flushPendingWrite}>
            {/* Label above the editor, like the labels of the other content types */}
            {!isMultiLanguage && (
                <Typography
                    component="label"
                    variant="body2"
                    color="text.secondary"
                    display="block"
                    mb={1}
                    data-testid="select-field-label"
                >
                    {label}
                </Typography>
            )}
            <FormControl fullWidth>
                <Stack gap={1} justifyContent="center" alignItems="flex-start">
                    {LanguageComponent}
                    <Box
                        width="100%"
                        minHeight="fit-content"
                        position="relative"
                        bgcolor="background.default"
                        pointerEvents={isDisabled ? 'none' : 'auto'}
                        // greyed out like the disabled text inputs, so a locked editor reads as such
                        color={isDisabled ? 'text.disabled' : undefined}
                    >
                        <ReactQuill
                            ref={reactQuillRef}
                            value={localValue}
                            onChange={handleTextChange}
                            modules={modules}
                            theme="snow"
                            readOnly={isDisabled || isProcessing}
                        />
                    </Box>
                    {readOnly && readOnlyMessage && (
                        <Typography variant="caption" color="text.secondary" ml={1.75}>
                            {readOnlyMessage}
                        </Typography>
                    )}
                </Stack>
            </FormControl>
        </Box>
    );
};

export default HtmlFieldElement;
